// Note: The indent rule is disabled due to a known ESLint bug with complex JSX patterns
// that causes "Maximum call stack size exceeded" errors. The file is formatted with Prettier.
import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  TooltipProps,
  Customized,
} from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';
import { ToasterNotice } from '../common/ToasterNotice';
import { ChartHeader } from './ChartHeader';
import { CannulationToasterContent } from './CannulationToasterContent';
import {
  CannulationBarData,
  CannulationBarSegment,
} from '../../models/CannulationScrewPlacementAdapter';
import { getActivityColor, getActivityLabel } from '../../utils/caseUtils';

export interface CannulationScrewPlacementChartProps {
  data: CannulationBarData[];
  avgTimePerLevel: string;
  activityTypes: string[];    // For legend rendering
  onBarClick?: (locus: string, segment: CannulationBarSegment) => void;
}

// Minimum bar width for touch accessibility (44px)
const MIN_BAR_WIDTH = 44;

// Minimum Y-axis domain in minutes to prevent floating x-axis when all values are low
const MIN_Y_AXIS_MINUTES = 6; // 6 minutes minimum

// Default Y-axis domain when no data is present
const DEFAULT_Y_AXIS_MINUTES = 6;

// Minimum number of columns on desktop to prevent overly wide bars
const MIN_COLUMNS_DESKTOP = 7;

// Tailwind md breakpoint in pixels
const MD_BREAKPOINT = 768;

// Detect if device has touch capability
const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
};

/**
 * CannulationScrewPlacementChart - A stacked bar chart showing cannulation
 * and screw placement efficiency by vertebral locus.
 */
export function CannulationScrewPlacementChart({
  data,
  avgTimePerLevel,
  activityTypes,
  onBarClick,
}: CannulationScrewPlacementChartProps): React.JSX.Element {
  const [selectedBarIndex, setSelectedBarIndex] = useState<number>(-1);
  const [isTouch] = useState<boolean>(isTouchDevice);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== 'undefined' && window.innerWidth >= MD_BREAKPOINT
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartLayoutRef = useRef<{
    axisX: number;
    barWidth: number;
    gap: number;
    chartLeft: number;
  } | null>(null);

  // Track screen size for minimum column padding
  useEffect(() => {
    const handleResize = (): void => {
      setIsDesktop(window.innerWidth >= MD_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Pad data with empty columns on desktop to prevent overly wide bars
  const chartData = React.useMemo(() => {
    if (!isDesktop || data.length >= MIN_COLUMNS_DESKTOP) {
      return data;
    }
    const paddingCount = MIN_COLUMNS_DESKTOP - data.length;
    const emptyColumns: CannulationBarData[] = Array.from(
      { length: paddingCount },
      () => ({
        locus: '',
        totalTime: 0,
        displayTotalTime: '',
        segments: [],
        // Create empty values for each activity type
        ...Object.fromEntries(activityTypes.map((type) => [type, 0])),
      })
    );
    return [...data, ...emptyColumns];
  }, [data, isDesktop, activityTypes]);

  // Get the currently selected bar data
  const selectedBarData = selectedBarIndex >= 0 ? data[selectedBarIndex] : null;

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return (): void => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Handle scroll detection for mobile (disable selection during scroll)
  const handleScroll = useCallback(() => {
    if (isTouch) {
      setIsScrolling(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    }
  }, [isTouch]);

  // Calculate chart width based on data count and minimum bar width
  const calculateChartWidth = (): number => {
    const minWidth = chartData.length * (MIN_BAR_WIDTH + 16); // bar width + gap
    return Math.max(minWidth, 300);
  };

  // Get the maximum time value for Y-axis scale (in minutes)
  const getMaxTimeMinutes = (): number => {
    if (data.length === 0) return DEFAULT_Y_AXIS_MINUTES;
    const maxMinutes = Math.max(...data.map((d) => d.totalTime));
    // Round up to nearest minute increment
    return Math.max(MIN_Y_AXIS_MINUTES, Math.ceil(maxMinutes));
  };

  // Generate Y-axis ticks in 1-minute increments for small values, or appropriate increments for larger
  const generateYAxisTicks = (): number[] => {
    const maxMinutes = getMaxTimeMinutes();
    const ticks: number[] = [];

    // Determine tick increment based on max value
    let increment = 1;
    if (maxMinutes > 30) {
      increment = 10;
    } else if (maxMinutes > 10) {
      increment = 5;
    } else if (maxMinutes > 6) {
      increment = 2;
    }

    for (let i = 0; i <= maxMinutes; i += increment) {
      ticks.push(i);
    }
    return ticks;
  };

  // Format Y-axis tick values (display in minutes or hours+minutes)
  const formatYAxisTick = (value: number): string => {
    if (value === 0) return '0m';
    if (value >= 60) {
      const hours = Math.floor(value / 60);
      const mins = value % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${value}m`;
  };

  // Handle click on chart area to select column
  const handleChartClick = (e: React.MouseEvent): void => {
    if (isTouch && isScrolling) return;

    const layout = chartLayoutRef.current;
    if (!layout || data.length === 0) return;

    // Get click position relative to the chart container
    const container = scrollContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left + container.scrollLeft;

    // Calculate which column was clicked
    const { axisX, barWidth, gap, chartLeft } = layout;
    const relativeX = clickX - chartLeft;

    // Each column spans barWidth + gap, starting at axisX - gap/2
    const columnWidth = barWidth + gap;
    const startX = axisX - gap / 2 - chartLeft;

    if (relativeX < startX) return; // Click was before first column

    const columnIndex = Math.floor((relativeX - startX) / columnWidth);

    // Only allow selection of real data columns (not padding columns)
    if (columnIndex >= 0 && columnIndex < data.length) {
      // Toggle selection (highlights column only, no video action)
      if (selectedBarIndex === columnIndex) {
        setSelectedBarIndex(-1);
      } else {
        setSelectedBarIndex(columnIndex);
      }
    }
  };

  // Navigation handlers for the toaster
  const handlePrevious = useCallback(() => {
    if (selectedBarIndex > 0) {
      setSelectedBarIndex(selectedBarIndex - 1);
    }
  }, [selectedBarIndex]);

  const handleNext = useCallback(() => {
    if (selectedBarIndex < data.length - 1) {
      setSelectedBarIndex(selectedBarIndex + 1);
    }
  }, [selectedBarIndex, data.length]);

  // Handle watch button click from toaster
  const handleWatchSegment = useCallback((segment: CannulationBarSegment) => {
    if (selectedBarData && onBarClick) {
      onBarClick(selectedBarData.locus, segment);
    }
  }, [selectedBarData, onBarClick]);

  // Handle bar segment click (for desktop video playback)
  const handleBarSegmentClick = useCallback((
    barData: CannulationBarData,
    activityType: string
  ) => {
    if (isTouch) return; // Mobile uses toaster via handleChartClick

    // Find the segment for this activity type
    const segment = barData.segments.find(s => s.activityType === activityType);
    if (segment && onBarClick) {
      onBarClick(barData.locus, segment);
    }
  }, [isTouch, onBarClick]);

  // Custom tooltip for desktop hover
  const CustomTooltip = ({
    active,
    payload,
  }: TooltipProps<ValueType, NameType>): React.JSX.Element | null => {
    if (!active || !payload || payload.length === 0) return null;

    const barData = payload[0].payload as CannulationBarData;

    return (
      <div
        className="rounded-[var(--radius)] border border-border p-3 shadow-elevation-sm"
        style={{
          backgroundColor: 'var(--popover)',
          color: 'var(--popover-foreground)',
        }}
      >
        <p className="font-semibold text-sm mb-2">{barData.locus}</p>
        {barData.segments.map((segment, index) => (
          <div key={index} className="flex items-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-xs text-medium-emphasis-font">{segment.label}:</span>
            <span className="text-sm font-semibold" style={{ color: segment.color }}>
              {segment.displayDuration}
            </span>
          </div>
        ))}
        <div className="mt-2 pt-2 border-t border-border">
          <p className="text-xs text-medium-emphasis-font">
            Total: <span className="font-semibold text-high-emphasis-font">
              {barData.displayTotalTime}
            </span>
          </p>
        </div>
      </div>
    );
  };

  // Custom legend renderer
  const renderLegend = (): React.JSX.Element => {
    return (
      <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-2 px-4 pb-4">
        {activityTypes.map((activity, index) => (
          <div key={index} className="flex items-center gap-1.5">
            <div
              className="w-3 h-3 rounded-sm flex-shrink-0"
              style={{ backgroundColor: getActivityColor(activity) }}
            />
            <span className="text-xs text-medium-emphasis-font whitespace-nowrap">
              {getActivityLabel(activity)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Determine if horizontal scroll is needed
  const needsScroll =
    calculateChartWidth() > (scrollContainerRef.current?.clientWidth || 0);
  const chartWidth = needsScroll ? calculateChartWidth() : '100%';

  // Get bar colors based on activity types present
  const barKeys = activityTypes.map((activity) => ({
    key: activity,
    color: getActivityColor(activity),
  }));

  return (
    <div
      className="rounded-[var(--radius-card)] border border-border flex flex-col"
      style={{ backgroundColor: 'var(--card)' }}
    >
      {/* Header Section */}
      <div className="px-4 pt-4">
        <ChartHeader
          title="Cannulation & Screw Placement Efficiency"
          timeLabel="Avg. Time Per Level"
          timeValue={avgTimePerLevel}
        />
      </div>

      {/* Chart Container with horizontal scroll support */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overflow-y-hidden"
        onScroll={handleScroll}
        onClick={handleChartClick}
        style={{ cursor: needsScroll ? 'grab' : 'default' }}
      >
        <div style={{ width: chartWidth, minWidth: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 16, left: 0, bottom: 5 }}
              barCategoryGap={isTouch ? 8 : 16}
            >
              <CartesianGrid
                strokeDasharray="0"
                vertical={false}
                stroke="var(--border)"
                strokeWidth={1}
              />

              {/* X-Axis: Locus labels */}
              <XAxis
                dataKey="locus"
                tick={{ fontSize: 11, fill: 'var(--medium-emphasis-font)' }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                height={30}
              />

              {/* Y-Axis: Time in minutes */}
              <YAxis
                ticks={generateYAxisTicks()}
                tickFormatter={formatYAxisTick}
                domain={[0, getMaxTimeMinutes()]}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
                tick={{ fontSize: 11, fill: 'var(--medium-emphasis-font)' }}
                width={40}
              />

              {/* Tooltip: Only shown on desktop hover */}
              {!isTouch && (
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ fill: 'var(--surface-200)', fillOpacity: 0.3 }}
                />
              )}

              {/* Hide default legend - we render custom legend */}
              <Legend content={() => null} />

              {/* Custom vertical lines between bars and column highlighting */}
              <Customized
                component={(props: {
                  xAxisMap?: Record<string, { x: number; width: number; bandSize: number }>;
                  offset?: { top: number; bottom: number; left: number; right: number };
                  height?: number;
                }) => {
                  const { xAxisMap, offset, height } = props;
                  if (!xAxisMap || !offset || !height || chartData.length < 1) return null;

                  const xAxis = Object.values(xAxisMap)[0];
                  if (!xAxis) return null;

                  const { x: axisX, width: axisWidth, bandSize } = xAxis;
                  const chartTop = offset.top;
                  const chartBottom = height - offset.bottom;
                  const chartHeight = chartBottom - chartTop;

                  const elements = [];
                  const barWidth = bandSize;
                  const totalWidth = axisWidth;
                  const gap = (totalWidth - barWidth * chartData.length) / chartData.length;

                  // Store layout for click handling
                  chartLayoutRef.current = {
                    axisX,
                    barWidth,
                    gap,
                    chartLeft: offset.left,
                  };

                  // Add column highlight for selected bar
                  if (selectedBarIndex >= 0) {
                    const columnX = axisX + (barWidth + gap) * selectedBarIndex - gap / 2;
                    const columnWidth = barWidth + gap;

                    elements.push(
                      <rect
                        key="column-highlight"
                        x={columnX}
                        y={chartTop}
                        width={columnWidth}
                        height={chartHeight}
                        fill="var(--surface-400)"
                      />
                    );
                  }

                  // Add vertical lines between bars
                  if (chartData.length >= 2) {
                    for (let i = 1; i < chartData.length; i++) {
                      const lineX = axisX + (barWidth + gap) * i - gap / 2;

                      elements.push(
                        <line
                          key={`separator-${i}`}
                          x1={lineX}
                          y1={chartTop}
                          x2={lineX}
                          y2={chartBottom}
                          stroke="var(--border)"
                          strokeWidth={1}
                        />
                      );
                    }

                    // Add right border line after the last bar
                    const rightLineX = axisX + totalWidth;
                    elements.push(
                      <line
                        key="separator-right"
                        x1={rightLineX}
                        y1={chartTop}
                        x2={rightLineX}
                        y2={chartBottom}
                        stroke="var(--border)"
                        strokeWidth={1}
                      />
                    );
                  }

                  return <g>{elements}</g>;
                }}
              />

              {/* Stacked Bar components for each activity type */}
              {barKeys.map((barKey, index) => (
                <Bar
                  key={barKey.key}
                  dataKey={barKey.key}
                  stackId="cannulation"
                  fill={barKey.color}
                  radius={index === barKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  style={{ cursor: 'pointer' }}
                  onClick={(data) => {
                    if (data?.payload) {
                      handleBarSegmentClick(data.payload as CannulationBarData, barKey.key);
                    }
                  }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend - Always visible */}
      {renderLegend()}

      {/* Toaster Notice for mobile (100% width, anchored to bottom) */}
      {isTouch && selectedBarData && (
        <ToasterNotice
          customContent={
            <CannulationToasterContent
              barData={selectedBarData}
              onWatchSegment={handleWatchSegment}
            />
          }
          onClose={() => setSelectedBarIndex(-1)}
          onPrev={handlePrevious}
          onNext={handleNext}
          hasPrev={selectedBarIndex > 0}
          hasNext={selectedBarIndex < data.length - 1}
        />
      )}
    </div>
  );
}

export default CannulationScrewPlacementChart;
